import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from "react";
import { useSearchParams } from "react-router-dom";
import { useDateRange, type DateRange } from "@/hooks/useDateRange";
import {
  clampAnalyticsDateRange,
  getDefaultAnalyticsDateRange,
  parseLocalDateParam,
  toAnalyticsDateParam,
} from "../utils/analyticsDateUtils";

type AnalyticsPageContextValue = {
  dateRange: DateRange;
  setDateRange: (range: DateRange) => void;
  resetDateRange: () => void;
};

const AnalyticsPageContext = createContext<AnalyticsPageContextValue | null>(
  null,
);

const ANALYTICS_FROM_PARAM = "analyticsFrom";
const ANALYTICS_TO_PARAM = "analyticsTo";

function parseDateParam(value: string | null): Date | undefined {
  if (!value) {
    return undefined;
  }

  return parseLocalDateParam(value);
}

function serializeDateParam(value: Date | undefined): string | null {
  if (!value || Number.isNaN(value.getTime())) {
    return null;
  }

  return toAnalyticsDateParam(value) ?? null;
}

function datesEqual(a: Date | undefined, b: Date | undefined): boolean {
  if (!a && !b) {
    return true;
  }
  if (!a || !b) {
    return false;
  }

  return a.getTime() === b.getTime();
}

export function AnalyticsPageProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    dateRange,
    setDateRange: setDateRangeState,
    resetDateRange: resetDateRangeState,
  } = useDateRange();

  const persistRangeToSearchParams = useCallback(
    (range: DateRange) => {
      setSearchParams((previous) => {
        const next = new URLSearchParams(previous);
        const fromParam = serializeDateParam(range.from);
        const toParam = serializeDateParam(range.to);

        if (fromParam) {
          next.set(ANALYTICS_FROM_PARAM, fromParam);
        } else {
          next.delete(ANALYTICS_FROM_PARAM);
        }

        if (toParam) {
          next.set(ANALYTICS_TO_PARAM, toParam);
        } else {
          next.delete(ANALYTICS_TO_PARAM);
        }

        return next;
      });
    },
    [setSearchParams],
  );

  const setDateRange = useCallback(
    (range: DateRange) => {
      const clamped = clampAnalyticsDateRange(range);
      setDateRangeState(clamped);
      persistRangeToSearchParams(clamped);
    },
    [persistRangeToSearchParams, setDateRangeState],
  );

  const resetDateRange = useCallback(() => {
    resetDateRangeState();
    persistRangeToSearchParams({ from: undefined, to: undefined });
  }, [persistRangeToSearchParams, resetDateRangeState]);

  useEffect(() => {
    const fromParam = searchParams.get(ANALYTICS_FROM_PARAM);
    const toParam = searchParams.get(ANALYTICS_TO_PARAM);

    if (!fromParam && !toParam) {
      if (!dateRange.from && !dateRange.to) {
        setDateRange(getDefaultAnalyticsDateRange());
      }
      return;
    }

    const parsed = clampAnalyticsDateRange({
      from: parseDateParam(fromParam),
      to: parseDateParam(toParam),
    });

    const parsedFromParam = toAnalyticsDateParam(parsed.from);
    const parsedToParam = toAnalyticsDateParam(parsed.to);
    const needsUrlCorrection =
      parsedFromParam !== fromParam || parsedToParam !== toParam;

    if (
      !needsUrlCorrection &&
      datesEqual(dateRange.from, parsed.from) &&
      datesEqual(dateRange.to, parsed.to)
    ) {
      return;
    }

    if (needsUrlCorrection) {
      setDateRange(parsed);
      return;
    }

    setDateRangeState(parsed);
  }, [
    dateRange.from,
    dateRange.to,
    searchParams,
    setDateRange,
    setDateRangeState,
  ]);

  const value = useMemo(
    () => ({
      dateRange,
      setDateRange,
      resetDateRange,
    }),
    [dateRange, setDateRange, resetDateRange],
  );

  return (
    <AnalyticsPageContext.Provider value={value}>
      {children}
    </AnalyticsPageContext.Provider>
  );
}

export function useAnalyticsPage(): AnalyticsPageContextValue {
  const context = useContext(AnalyticsPageContext);

  if (!context) {
    throw new Error(
      "useAnalyticsPage must be used within AnalyticsPageProvider",
    );
  }

  return context;
}
